import type { AdminDashboard, AdminMe, AdminPaymentRow, AdminPeriod } from '@trendator/shared';
import { useState } from 'react';
import { Link } from '../components/Layout';
import { Card, CardTitle, Dot, ErrorBox, Label, Loading, Segmented, Table, type Tone } from '../components/ui';
import { api } from '../lib/api';
import { num, rub, seconds, userName } from '../lib/format';
import { usePoll, useLoad } from '../lib/hooks';

const PERIODS = [
  { value: 'today', label: 'Сегодня' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
] as const;
const PREV: Record<AdminPeriod, string> = { today: 'к вчера', '7d': 'к прошлой неделе', '30d': 'к прошлому месяцу' };
const PERIOD_LABEL: Record<AdminPeriod, string> = { today: 'сегодня', '7d': 'за 7 дней', '30d': 'за 30 дней' };

function greeting(): string {
  const h = Number(new Date().toLocaleString('ru-RU', { hour: 'numeric', hour12: false, timeZone: 'Europe/Moscow' }));
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

const pctText = (n: number) => `${n.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;

export function Dashboard({ me }: { me: AdminMe }) {
  const [period, setPeriod] = useState<AdminPeriod>('today');
  const d = useLoad(() => api.get<AdminDashboard>(`/dashboard?period=${period}`), [period]);
  const pays = useLoad(() => api.get<{ items: AdminPaymentRow[] }>('/payments?filter=paid'));
  usePoll(() => void d.reload(), 30_000, true);

  const now = new Date().toLocaleString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="first-letter:uppercase">{now} МСК</Label>
          <h1 className="m-0 text-[26px] font-extrabold tracking-tight lg:text-[30px]">
            {greeting()}, {me.name.split(' ')[0]}
          </h1>
        </div>
        <Segmented value={period} onChange={setPeriod} options={PERIODS} />
      </div>

      {d.error && !d.data ? <ErrorBox text={d.error} onRetry={() => void d.reload()} /> : null}
      {!d.data ? (
        d.loading ? <Loading /> : null
      ) : (
        <Body d={d.data} period={period} pays={pays.data?.items.slice(0, 4) ?? []} />
      )}
    </>
  );
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className="inline-flex h-8 shrink-0 items-center gap-2 rounded-full border border-line bg-field px-3 text-[13px] font-medium whitespace-nowrap text-text2">
      <Dot tone={tone} />
      {children}
    </span>
  );
}

function Body({ d, period, pays }: { d: AdminDashboard; period: AdminPeriod; pays: AdminPaymentRow[] }) {
  const { health: h, kpi: k } = d;
  const delta = k.revenuePrev > 0 ? Math.round(((k.revenue - k.revenuePrev) / k.revenuePrev) * 1000) / 10 : null;
  const maxDay = Math.max(1, ...d.days.map((x) => Math.max(x.revenue, x.costRub)));
  const f = d.funnel;
  const funnel = [
    { label: 'Запустили бота', value: f.started },
    { label: 'Прошли онбординг', value: f.consent },
    { label: 'Сделали генерацию', value: f.generated },
    { label: 'Пополнили баланс', value: f.paid },
  ];
  const drops = funnel.slice(1).map((s, i) => ({ from: funnel[i]!.label, to: s.label, lost: funnel[i]!.value - s.value }));
  const worst = drops.reduce((a, b) => (b.lost > a.lost ? b : a), drops[0]!);

  return (
    <>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
        <Pill tone={!h.kie.configured ? 'bad' : h.kie.enabled ? 'ok' : 'warn'}>
          kie.ai {!h.kie.configured ? 'не подключён' : h.kie.enabled ? 'работает' : 'выключен'}
          {h.kie.credits !== null ? ` · ${num(Math.round(h.kie.credits))} кредитов` : ''}
        </Pill>
        <Pill tone={!h.fal.configured ? 'idle' : h.fal.enabled ? 'ok' : 'warn'}>
          Запасной fal.ai {!h.fal.configured ? 'не подключён' : h.fal.enabled ? 'готов' : 'выключен'}
        </Pill>
        {h.payments.length ? (
          h.payments.map((p) => (
            <Pill key={p.provider} tone={p.test ? 'warn' : 'ok'}>
              {p.provider === 'rollypay' ? 'RollyPay' : 'Crypto Pay'} {p.test ? 'тест' : 'ок'}
            </Pill>
          ))
        ) : (
          <Pill tone="bad">Оплата не подключена</Pill>
        )}
        <Pill tone={h.queues ? 'run' : 'bad'}>{h.queues ? `Очередь: фото ${h.queues.photo} · видео ${h.queues.video}` : 'Очередь недоступна'}</Pill>
        <Pill tone={h.stuckGenerations ? 'warn' : 'ok'}>Зависших задач {h.stuckGenerations}</Pill>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-4">
        <Card className="flex flex-col gap-2.5 border-accent/28 bg-[linear-gradient(160deg,rgba(45,180,255,0.16),rgba(45,180,255,0.02)_60%),#0B111B]">
          <Label className="text-[#A9D9FF]">Выручка</Label>
          <b className="text-[34px] leading-none font-extrabold tracking-tight">{rub(k.revenue)}</b>
          <span className="text-[13px] text-muted">
            {delta === null ? 'нет данных для сравнения' : (
              <>
                <span className={delta >= 0 ? 'font-semibold text-ok' : 'font-semibold text-bad'}>
                  {delta >= 0 ? '+' : ''}
                  {pctText(delta)}
                </span>{' '}
                {PREV[period]}
              </>
            )}
          </span>
        </Card>
        <Card className="flex flex-col gap-2.5">
          <Label>Маржа после себестоимости</Label>
          <b className="text-[34px] leading-none font-extrabold tracking-tight">{rub(k.marginRub)}</b>
          <span className="flex items-center gap-2.5">
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/8">
              <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.max(0, Math.min(100, k.marginPct))}%` }} />
            </span>
            <span className="text-[13px] font-semibold text-text2">{k.marginPct}%</span>
          </span>
        </Card>
        <Card className="flex flex-col gap-2.5">
          <Label>Генерации</Label>
          <b className="text-[34px] leading-none font-extrabold tracking-tight">{num(k.generations)}</b>
          <span className="text-[13px] text-muted">
            платных {num(k.paidGenerations)} · бесплатных {num(k.freeGenerations)} · возвратов {num(k.failedGenerations)}
          </span>
        </Card>
        <Card className="flex flex-col gap-2.5">
          <Label>Новые юзеры</Label>
          <b className="text-[34px] leading-none font-extrabold tracking-tight">{num(k.newUsers)}</b>
          <span className="text-[13px] text-muted">
            оплатили {num(k.payingUsers)}
            {k.newUsers ? ` · конверсия ${pctText(Math.round((f.paid / Math.max(1, f.started)) * 1000) / 10)}` : ''}
          </span>
        </Card>
      </div>

      <div className="flex flex-wrap gap-4">
        <Card className="flex min-w-0 flex-[2_1_520px] flex-col gap-[18px]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-base font-bold">Выручка и себестоимость</span>
              <Label>последние 14 дней, ₽</Label>
            </div>
            <div className="flex gap-3.5 text-[13px] text-soft">
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-accent" />
                Выручка
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-[#2A3A52]" />
                Себестоимость
              </span>
            </div>
          </div>
          <div className="flex gap-2.5">
            <div className="flex h-[220px] flex-col justify-between pb-[22px] text-right text-[11px] text-dim">
              <span>{num(maxDay)}</span>
              <span>{num(Math.round(maxDay / 2))}</span>
              <span>0</span>
            </div>
            <div className="relative flex h-[220px] min-w-0 flex-1 items-end gap-1.5 border-b border-white/8 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px)] bg-size-[100%_99px] sm:gap-2">
              {d.days.map((x, i) => {
                const last = i === d.days.length - 1;
                const costH = (Math.min(x.costRub, x.revenue || x.costRub) / maxDay) * 198;
                const revH = (Math.max(0, x.revenue - x.costRub) / maxDay) * 198;
                return (
                  <div key={x.day} className="relative flex h-full min-w-0 flex-1 flex-col justify-end" title={`${x.day}: выручка ${rub(x.revenue)}, себестоимость ${rub(x.costRub)}`}>
                    <div className="rounded-t-md" style={{ height: revH, background: last ? '#2DB4FF' : 'rgba(45,180,255,0.55)' }} />
                    <div className={revH ? '' : 'rounded-t-md'} style={{ height: costH, background: '#2A3A52' }} />
                    <span className="absolute inset-x-0 -bottom-5 text-center text-[11px] text-dim">{Number(x.day.slice(8))}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="h-1.5" />
        </Card>

        <Card className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-base font-bold">Воронка {PERIOD_LABEL[period]}</span>
            <Label>от /start до первой оплаты</Label>
          </div>
          {funnel.map((s, i) => {
            const pct = f.started ? (s.value / f.started) * 100 : 0;
            return (
              <div key={s.label} className="flex flex-col gap-[7px]">
                <div className="flex justify-between text-sm">
                  <span className="text-text2">{s.label}</span>
                  <span>
                    <b>{num(s.value)}</b> {i > 0 ? <span className="text-muted">{pctText(Math.round(pct * 10) / 10)}</span> : null}
                  </span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-white/6">
                  <div className="h-full rounded-full" style={{ width: `${i === 0 ? (f.started ? 100 : 0) : pct}%`, background: i === 3 ? '#FFB547' : `rgba(45,180,255,${1 - i * 0.2})` }} />
                </div>
              </div>
            );
          })}
          {f.started && worst && worst.lost > 0 ? (
            <div className="mt-auto rounded-[14px] bg-accent/8 px-3.5 py-3 text-[13px] text-[#A9D9FF]">
              Самый большой провал — между «{worst.from.toLowerCase()}» и «{worst.to.toLowerCase()}»: −{num(worst.lost)}.
            </div>
          ) : null}
        </Card>
      </div>

      <div className="flex flex-wrap gap-4">
        <Card className="flex min-w-0 flex-[2_1_520px] flex-col gap-3.5">
          <CardTitle right={<Link to="/trends" className="text-sm font-semibold">Все тренды</Link>}>Топ трендов</CardTitle>
          {d.trends.length === 0 ? (
            <span className="py-6 text-sm text-muted">Генераций по трендам {PERIOD_LABEL[period]} пока нет.</span>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Тренд</th>
                  <th className="text-right!">Генераций</th>
                  <th className="text-right!">Выручка</th>
                  <th className="text-right!">Маржа</th>
                  <th className="text-right!">Брак</th>
                </tr>
              </thead>
              <tbody>
                {d.trends.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/trends/${t.id}`} className="flex items-center gap-3 text-white">
                        <span className="size-9 shrink-0 rounded-[10px]" style={{ background: t.coverGradient }} />
                        <span className="flex flex-col">
                          <span className="font-semibold">{t.title}</span>
                          <span className="text-xs text-muted">{t.kind === 'photo' ? 'фото' : t.kind === 'video' ? 'видео' : 'песня'}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="text-right">{num(t.generations)}</td>
                    <td className="text-right font-semibold">{rub(t.revenue)}</td>
                    <td className="text-right">{t.revenue ? `${Math.round((t.marginRub / t.revenue) * 100)}%` : '—'}</td>
                    <td className={t.failedPct > 3 ? 'text-right text-warn' : 'text-right'}>{pctText(t.failedPct)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card className="flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
          <CardTitle right={d.alerts.length ? <span className="rounded-full bg-warn/14 px-2.5 py-0.5 text-[13px] font-bold text-warn">{d.alerts.length}</span> : null}>
            Требует внимания
          </CardTitle>
          {d.alerts.length === 0 ? <span className="text-sm text-muted">Всё спокойно 👌</span> : null}
          {d.alerts.map((a) => (
            <div key={a.title} className="flex items-start gap-3 rounded-[14px] bg-field p-3">
              <Dot tone={a.level === 'error' ? 'bad' : 'warn'} className="mt-1.5" />
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-sm font-semibold">{a.title}</span>
                <span className="text-[13px] text-muted">{a.text}</span>
              </span>
              {a.href ? (
                <Link to={a.href} className="text-[13px] font-semibold whitespace-nowrap">
                  Разобрать
                </Link>
              ) : null}
            </div>
          ))}
        </Card>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4">
        <Card className="flex flex-col gap-3">
          <Label>Качество {PERIOD_LABEL[period]}</Label>
          <div className="flex gap-5">
            <Stat value={pctText(d.quality.failedPct)} label="брак" />
            <Stat value={pctText(d.quality.refundPct)} label="возвраты" />
            <Stat value={seconds(d.quality.medianSec)} label="медиана" />
          </div>
        </Card>
        <Card className="flex flex-col gap-3">
          <Label>Последние оплаты</Label>
          <div className="flex flex-col gap-2.5 text-sm">
            {pays.length === 0 ? <span className="text-muted">Пока нет</span> : null}
            {pays.map((p) => (
              <span key={p.id} className="flex justify-between gap-3">
                <Link to={`/users/${p.user.id}`} className="truncate text-white">
                  {userName(p.user)} · {p.provider === 'rollypay' ? 'RollyPay' : 'USDT'}
                  {p.test ? ' · тест' : ''}
                </Link>
                <b className="text-ok">+{rub(p.amount)}</b>
              </span>
            ))}
          </div>
        </Card>
        <Card className="flex flex-col gap-3 opacity-60">
          <Label>Партнёрка</Label>
          <span className="text-sm text-muted">Начисления и заявки на вывод появятся на неделе 6.</span>
        </Card>
      </div>
    </>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <span className="flex flex-col gap-0.5">
      <b className="text-2xl">{value}</b>
      <span className="text-xs text-muted">{label}</span>
    </span>
  );
}
